import React, { useEffect, useState } from 'react';

import { Paper, Stack, Typography } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistRuleCard from '../../reportCards/ChecklistRuleCard';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import type { ManualOverrideState } from '../../types';

import type { CampaignChecklistRow } from './types';

interface Props {
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<CampaignChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function CampaignReportCard({
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpandedRows(rows.reduce<Record<string, boolean>>((nextRows, row) => ({ ...nextRows, [row.id]: true }), {}));
  }, [rows]);

  return (
    <ReportCard>
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {rows.length === 0 ? (
          <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
            <Typography variant="body2">No Campaign rows match this view.</Typography>
          </Paper>
        ) : (
          rows.map((row) => {
            const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
            const approved = isRowApproved(row, manualOverrides);
            const isExpanded = expandedRows[row.id] ?? true;

            return (
              <ChecklistRuleCard
                key={row.id}
                approved={approved}
                approvalNote={row.approvalNote}
                dataPoints={row.dataPoints}
                expanded={isExpanded}
                manuallyApproved={manuallyApproved}
                rule={row.rule}
                summary={row.summary}
                title={row.packageName ? `${row.field}: ${row.packageName}` : row.field}
                onApprovalChange={() => onApprovalChange(row.id)}
                onUnapprovalChange={() => onUnapprovalChange(row.id)}
                onToggleExpanded={() => setExpandedRows((currentRows) => ({ ...currentRows, [row.id]: !isExpanded }))}
              />
            );
          })
        )}
      </Stack>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="Campaign row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
